import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

export interface CodigoAnomalia {
  id: string;
  codigo: string;
  descripcion: string;
  tipo_sistema: string;
  severidad: string;
  solucion_sugerida?: string;
  referencia_normativa?: string;
  justificacion_normativa?: string;
}

export interface AnomaliaElemento {
  id: string;
  elemento_id: string;
  codigo_anomalia: string;
  descripcion?: string;
  fecha_deteccion: string;
  tecnico_id: string;
  resuelta: boolean;
  fecha_resolucion?: string;
  notas_resolucion?: string;
  codigo_info?: CodigoAnomalia | null;
}

export const useAnomalias = () => {
  const [codigosAnomalias, setCodigosAnomalias] = useState<CodigoAnomalia[]>([]);
  const [anomaliasElementos, setAnomaliasElementos] = useState<AnomaliaElemento[]>([]);
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();

  // Cargar códigos de anomalías, opcionalmente filtrados por tipo de sistema
  const cargarCodigosAnomalias = async (tipoSistema?: string) => {
    setLoading(true);
    try {
      let query = supabase
        .from('codigos_anomalias')
        .select('*')
        .order('codigo');

      if (tipoSistema) {
        query = query.eq('tipo_sistema', tipoSistema);
      }

      const { data, error } = await query;

      if (error) throw error;
      setCodigosAnomalias(data || []);
    } catch (error) {
      console.error('Error cargando códigos de anomalías:', error);
      toast({
        title: "Error",
        description: "No se pudieron cargar los códigos de anomalías",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  // Cargar anomalías de elementos con consulta simplificada
  const cargarAnomaliasElementos = async (elementoId?: string) => {
    setLoading(true);
    try {
      // Simplified query - load anomalies first
      let anomaliesQuery = supabase
        .from('anomalias_elementos')
        .select('*')
        .order('fecha_deteccion', { ascending: false });

      if (elementoId) {
        anomaliesQuery = anomaliesQuery.eq('elemento_id', elementoId);
      }

      const { data: anomaliesData, error: anomaliesError } = await anomaliesQuery;
      if (anomaliesError) throw anomaliesError;

      // Load codigo info separately to avoid join issues
      const codigosMap = new Map();
      if (anomaliesData && anomaliesData.length > 0) {
        const uniqueCodigos = [...new Set(anomaliesData.map(a => a.codigo_anomalia))];
        const { data: codigosData } = await supabase
          .from('codigos_anomalias')
          .select('*')
          .in('codigo', uniqueCodigos);
        
        if (codigosData) {
          codigosData.forEach(codigo => {
            codigosMap.set(codigo.codigo, codigo);
          });
        }
      }

      // Combine the data
      const enrichedAnomalies = (anomaliesData || []).map(anomalia => ({
        ...anomalia,
        codigo_info: codigosMap.get(anomalia.codigo_anomalia) || null
      }));
      
      console.log('Loaded anomalies for elements:', enrichedAnomalies.length);
      console.log('Anomalies data sample:', enrichedAnomalies.slice(0, 2));
      
      setAnomaliasElementos(enrichedAnomalies);
      return enrichedAnomalies; // Return data for immediate use
    } catch (error) {
      console.error('Error cargando anomalías de elementos:', error);
      toast({
        title: "Error",
        description: "No se pudieron cargar las anomalías",
        variant: "destructive",
      });
      setAnomaliasElementos([]); // Ensure empty state on error
      return [];
    } finally {
      setLoading(false);
    }
  };

  // Crear nueva anomalía
  const crearAnomalia = async (
    elementoId: string,
    codigoAnomalia: string,
    descripcion?: string
  ) => {
    setLoading(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Usuario no autenticado');

      const { data, error } = await supabase
        .from('anomalias_elementos')
        .insert([
          {
            elemento_id: elementoId,
            codigo_anomalia: codigoAnomalia,
            descripcion,
            tecnico_id: user.id,
          }
        ])
        .select()
        .single();

      if (error) throw error;

      toast({
        title: "Anomalía registrada",
        description: `Código ${codigoAnomalia} registrado correctamente`,
      });

      // Force reload of all anomalies to ensure sync across components
      const updatedAnomalies = await cargarAnomaliasElementos();
      console.log('Anomaly created, reloaded anomalies:', updatedAnomalies.length);
      
      return data;
    } catch (error) {
      console.error('Error creando anomalía:', error);
      toast({
        title: "Error",
        description: "No se pudo registrar la anomalía",
        variant: "destructive",
      });
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // Resolver anomalía
  const resolverAnomalia = async (anomaliaId: string, notasResolucion?: string) => {
    setLoading(true);
    try {
      const { error } = await supabase
        .from('anomalias_elementos')
        .update({
          resuelta: true,
          fecha_resolucion: new Date().toISOString(),
          notas_resolucion: notasResolucion,
        })
        .eq('id', anomaliaId);

      if (error) throw error;

      toast({
        title: "Anomalía resuelta",
        description: "La anomalía ha sido marcada como resuelta",
      });

      await cargarAnomaliasElementos();
    } catch (error) {
      console.error('Error resolviendo anomalía:', error);
      toast({
        title: "Error",
        description: "No se pudo resolver la anomalía",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  // Eliminar anomalía
  const eliminarAnomalia = async (anomaliaId: string) => {
    setLoading(true);
    try {
      const { error } = await supabase
        .from('anomalias_elementos')
        .delete()
        .eq('id', anomaliaId);

      if (error) throw error;

      toast({
        title: "Anomalía eliminada",
        description: "La anomalía ha sido eliminada correctamente",
      });

      await cargarAnomaliasElementos();
    } catch (error) {
      console.error('Error eliminando anomalía:', error);
      toast({
        title: "Error",
        description: "No se pudo eliminar la anomalía",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  // Cargar códigos por categoría de sistema
  const cargarCodigosPorCategoria = async (categoria: 'seguridad' | 'pci') => {
    setLoading(true);
    try {
      const { getSystemTypesByCategory } = await import('@/lib/systemTypeConstants');
      const systemTypes = Object.keys(getSystemTypesByCategory(categoria));
      
      const { data, error } = await supabase
        .from('codigos_anomalias')
        .select('*')
        .in('tipo_sistema', systemTypes)
        .order('codigo');

      if (error) throw error;
      setCodigosAnomalias(data || []);
    } catch (error) {
      console.error('Error cargando códigos por categoría:', error);
      toast({
        title: "Error",
        description: "No se pudieron cargar los códigos de anomalías",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  return {
    codigosAnomalias,
    anomaliasElementos,
    loading,
    cargarCodigosAnomalias,
    cargarCodigosPorCategoria,
    cargarAnomaliasElementos,
    crearAnomalia,
    resolverAnomalia,
    eliminarAnomalia,
  };
};